import { DestroyRef, Injectable, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  RouterEvent,
  Router,
} from "@angular/router";
import { filter } from "rxjs";

const MIN_DISPLAY_MS = 400;

@Injectable({ providedIn: "root" })
export class GlobalLoadingService {
  public readonly isLoading = signal<boolean>(false);

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private isNavigating = false;
  private activeRequests = 0;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private shownAt: number | null = null;

  constructor() {
    this.router.events
      .pipe(
        filter(
          (e): e is NavigationStart => e instanceof NavigationStart,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.onNavigationStart());

    this.router.events
      .pipe(
        filter(
          (e): e is NavigationEnd | NavigationCancel | NavigationError =>
            e instanceof NavigationEnd ||
            e instanceof NavigationCancel ||
            e instanceof NavigationError,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.onNavigationEnd());
  }

  /**
   * Llamado por el interceptor al iniciar un request.
   * Solo rastrea el request si el loader ya esta visible (durante navegacion).
   */
  public registrarRequest(): void {
    if (!this.isLoading()) {
      return;
    }
    this.activeRequests++;
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  /**
   * Llamado por el interceptor al finalizar un request (exito o error).
   */
  public desregistrarRequest(): void {
    if (this.activeRequests === 0) {
      return;
    }
    this.activeRequests--;
    this.tryHide();
  }

  private onNavigationStart(): void {
    this.isNavigating = true;

    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    if (!this.isLoading()) {
      this.shownAt = Date.now();
      this.isLoading.set(true);
    }
  }

  private onNavigationEnd(): void {
    this.isNavigating = false;
    // setTimeout(0): esperar un tick para que ngOnInit de la pantalla nueva
    // tenga oportunidad de llamar registrarRequest() antes de evaluar el hide.
    setTimeout(() => this.tryHide(), 0);
  }

  private tryHide(): void {
    if (this.isNavigating) {
      return;
    }
    if (this.activeRequests > 0) {
      return;
    }
    if (!this.isLoading()) {
      return;
    }
    if (this.hideTimer !== null) {
      return;
    }

    const elapsed =
      this.shownAt !== null ? Date.now() - this.shownAt : MIN_DISPLAY_MS;
    const remaining = Math.max(MIN_DISPLAY_MS - elapsed, 0);

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      if (this.activeRequests === 0 && !this.isNavigating) {
        this.shownAt = null;
        this.isLoading.set(false);
      }
    }, remaining);
  }
}
